import {
  LitElement, html, nothing,
  type TemplateResult, type PropertyValues,
} from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { live } from 'lit/directives/live.js';
import type {
  Placement,
  Size,
  SelectOption,
  AutocompleteCompleteEventDetail,
  AutocompleteSelectEventDetail,
  AutocompleteUnselectEventDetail,
  AutocompleteClearEventDetail,
} from '../types.js';
import { emit } from '../utils/dom.utils.js';
import { PlacementController } from '../utils/placement.controller.js';
import { VirtualScrollController } from '../utils/virtual-scroll.controller.js';
import { FormAssociatedMixin }      from '../utils/form-associated.mixin.js';
import { FormValidationController } from '../utils/form-validation.controller.js';
import styles from './styles.js';

export type AutocompleteSize       = Size;
export type AutocompleteVariant    = 'outlined' | 'filled';
export type AutocompleteFloatLabel = 'over' | 'in' | 'on';

/**
 * uwc-autocomplete
 *
 * Full-featured type-ahead / combobox component.
 *
 * Usage pattern:
 *  1. User types  →  component fires `uwc-complete` with { query }.
 *  2. Host filters data and sets the `suggestions` property.
 *  3. Panel renders the updated suggestions. Panel closes on selection.
 *
 * Features:
 *  - Single and multiple selection (chip/token UI)
 *  - External suggestion model (`suggestions` property set by host)
 *  - Async loading state with skeleton rows
 *  - Dropdown button (show all suggestions on button click)
 *  - Force-selection (reverts input if not matching a suggestion)
 *  - Virtual scrolling for large suggestion lists
 *  - Float label: over | in | on
 *  - Size variants: sm | md | lg
 *  - Visual variants: outlined | filled
 *  - Invalid / error state
 *  - Fluid (full-width) mode
 *  - Option groups (non-selectable headers)
 *  - Clear (×) button
 *  - Prefix / suffix icon slots
 *  - Panel header / footer slots
 *  - Custom `renderItem` property
 *  - Min-length threshold and debounce delay
 *  - Full keyboard navigation (↑↓ Home End Enter Escape Tab)
 *  - ARIA combobox pattern (aria-expanded, aria-autocomplete, aria-activedescendant)
 *
 * @attr suggestions           - SelectOption[] — updated by host after uwc-complete.
 * @attr value                 - Currently selected SelectOption (single) or SelectOption[] (multiple).
 * @attr placeholder           - Input placeholder text.
 * @attr label                 - Label text used with float-label.
 * @attr multiple              - Enable multi-select (chips UI).
 * @attr loading               - Show skeleton loading rows in the panel.
 * @attr disabled              - Disable the entire component.
 * @attr dropdown              - Show a dropdown button to open the panel without typing.
 * @attr show-clear            - Show clear (×) button when a value is selected.
 * @attr force-selection       - Revert input to selected label if no suggestion matches on blur.
 * @attr min-length            - Min characters before uwc-complete fires. Default: 1.
 * @attr delay                 - Debounce delay (ms) before uwc-complete fires. Default: 300.
 * @attr scroll-height         - Max panel height in px. Default: 220.
 * @attr virtual               - Enable virtual scrolling.
 * @attr item-size             - Row height in px for virtual scroll. Default: 38.
 * @attr empty-message         - Message shown when suggestions is empty. Default: "No results found."
 * @attr placement             - Panel placement. Default: bottom-start.
 * @attr offset                - Gap between input and panel (px). Default: 4.
 * @attr max-selected-labels   - Max chips shown before overflow. Default: 3.
 * @attr float-label           - Float label mode: over | in | on.
 * @attr variant               - Visual variant: outlined (default) | filled.
 * @attr size                  - Size preset: sm | md | lg.
 * @attr invalid               - Mark field as invalid.
 * @attr fluid                 - Stretch to full container width.
 *
 * @property renderItem - `(item: SelectOption, index: number) => TemplateResult`
 *   Custom row renderer replacing the built-in option markup.
 *
 * @fires uwc-complete  — detail: { query, originalEvent } — fire when user types.
 * @fires uwc-select    — detail: { value: SelectOption, originalEvent }
 * @fires uwc-unselect  — detail: { value: SelectOption, originalEvent } (multiple mode)
 * @fires uwc-change    — detail: { value } — fires on every value change
 * @fires uwc-clear     — detail: { previousValue }
 * @fires uwc-focus
 * @fires uwc-blur
 * @fires uwc-show
 * @fires uwc-hide
 * @fires uwc-dropdown-click — dropdown button was clicked (detail: { query })
 *
 * @slot prefix - Icon / content before the input text.
 * @slot suffix - Icon / content after the actions area.
 * @slot header - Content above the suggestions list.
 * @slot footer - Content below the suggestions list.
 *
 * @csspart input-root - Outer wrapper that holds the chips, input, and action buttons.
 * @csspart input - The native `<input>` element used for text entry.
 * @csspart chip - Each selected-value chip in multiple mode.
 * @csspart clear-btn - The × button that clears the current selection.
 * @csspart dropdown-btn - The chevron button that opens the panel without typing.
 * @csspart panel - The floating suggestion list container.
 * @csspart option - Each individual suggestion row inside the panel.
 * @csspart group-header - Label row that separates grouped suggestion sections.
 * @csspart empty - Message shown inside the panel when suggestions is empty.
 *
 * @cssprop --uwc-ac-bg - Input background color. Default: surface token.
 * @cssprop --uwc-ac-border - Input border color. Default: border-control token.
 * @cssprop --uwc-ac-radius - Input corner radius. Default: radius-md token.
 * @cssprop --uwc-ac-color - Input text color. Default: text-default token.
 * @cssprop --uwc-ac-focus-border - Border color when the input is focused. Default: primary token.
 * @cssprop --uwc-ac-disabled-bg - Background when the component is disabled. Default: surface-muted token.
 * @cssprop --uwc-ac-chip-bg - Background of selected-value chips in multiple mode. Default: primary-subtle token.
 * @cssprop --uwc-ac-chip-color - Text color of selected-value chips. Default: primary token.
 * @cssprop --uwc-ac-panel-bg - Background of the suggestion panel. Default: surface token.
 * @cssprop --uwc-ac-panel-border - Border color of the suggestion panel. Default: border-control token.
 * @cssprop --uwc-ac-panel-radius - Corner radius of the suggestion panel. Default: radius-md token.
 * @cssprop --uwc-ac-panel-shadow - Box shadow of the suggestion panel. Default: shadow-lg token.
 * @cssprop --uwc-ac-option-hover-bg - Background of a suggestion row on hover. Default: surface-hover token.
 * @cssprop --uwc-ac-option-selected-bg - Background of the currently selected option row. Default: primary-subtle token.
 * @cssprop --uwc-ac-option-selected-color - Text color of the currently selected option row. Default: primary token.
 * @cssprop --uwc-ac-z - `z-index` of the floating panel. Default: 1000.
 * @cssprop --uwc-ac-duration - Transition duration for panel open/close animation. Default: duration-base token.
 */
export class UwcAutocomplete extends FormAssociatedMixin(LitElement) {

  static override styles = [styles];

  private _validation = new FormValidationController(this);

  // ── Props ──────────────────────────────────────────────────────────────────

  /** Suggestions provided by the host after handling uwc-complete. */
  @property({ type: Array })                                      suggestions:      SelectOption[]            = [];
  /** Currently selected value. SelectOption for single, SelectOption[] for multiple. */
  @property({ attribute: false })                                 value:            SelectOption | SelectOption[] | null = null;
  /** Preferred position of the suggestion panel relative to the input. */
  @property({ type: String })                                     placement:        Placement                 = 'bottom-start';
  /** Distance in pixels between the input and the suggestion panel. */
  @property({ type: Number })                                     offset                                      = 4;
  /** Placeholder text shown in the input when empty. */
  @property({ type: String })                                     placeholder       = '';
  /** Allow selecting multiple values — selected items appear as chips inside the input. */
  @property({ type: Boolean, reflect: true })                     multiple          = false;
  /** Show a loading spinner inside the panel while suggestions are being fetched. */
  @property({ type: Boolean, reflect: true })                     loading           = false;
  /** Disables all pointer and keyboard interaction. */
  @property({ type: Boolean, reflect: true })                     disabled          = false;
  /** Show a dropdown arrow button that opens the panel without typing. */
  @property({ type: Boolean })                                    dropdown          = false;
  /** Show a clear button to reset the selection. */
  @property({ type: Boolean, attribute: 'show-clear' })           showClear         = false;
  /** Prevent free-text input — only values from the suggestions list are accepted. */
  @property({ type: Boolean, attribute: 'force-selection' })      forceSelection    = false;
  /** Minimum number of characters required before firing `uwc-complete`. */
  @property({ type: Number, attribute: 'min-length' })            minLength         = 1;
  /** Debounce delay in milliseconds between the last keystroke and the `uwc-complete` event. */
  @property({ type: Number })                                     delay             = 300;
  /** Maximum height in pixels of the suggestion panel before it scrolls. */
  @property({ type: Number, attribute: 'scroll-height' })         override scrollHeight = 220;
  /** Enable virtual scrolling for large suggestion lists (requires `item-size`). */
  @property({ type: Boolean })                                    virtual           = false;
  /** Fixed row height in pixels used by the virtual scroller to calculate scroll position. */
  @property({ type: Number, attribute: 'item-size' })             itemSize          = 34;
  /** Message displayed inside the panel when the suggestions array is empty. */
  @property({ type: String, attribute: 'empty-message' })         emptyMessage      = 'No results found.';
  /** Maximum number of selected chips to show before collapsing to a count label. */
  @property({ type: Number, attribute: 'max-selected-labels' })   maxSelectedLabels = 3;
  /** Visible label rendered above the input field. */
  @property({ type: String })                                     label             = '';
  /** Float-label behaviour — `'over'` overlaps the input, `'on'` sits on the border. */
  @property({ type: String, attribute: 'float-label', reflect: true })
                                                                  floatLabel:       AutocompleteFloatLabel | null = null;
  /** Visual style of the input — `'outlined'` (default), `'filled'`, or `'plain'`. */
  @property({ type: String, reflect: true })                      variant:          AutocompleteVariant       = 'outlined';
  /** Size of the component — `'small'`, `'medium'` (default), or `'large'`. */
  @property({ type: String, reflect: true })                      size:             AutocompleteSize          = 'medium';
  /** Mark the input as invalid, applying error styling. */
  @property({ type: Boolean, reflect: true })                     invalid           = false;
  /** Require a selection. */
  @property({ type: Boolean, reflect: true })                     required          = false;
  /** Stretch the component to fill its container width. */
  @property({ type: Boolean, reflect: true })                     fluid             = false;

  /**
   * Custom item renderer. Receives `(item, absoluteIndex)` and returns a
   * TemplateResult filling the row content (icon, label, badge, etc.).
   */
  renderItem?: (item: SelectOption, index: number) => TemplateResult;

  // ── Internal state ─────────────────────────────────────────────────────────

  @state() private _inputValue    = '';
  @state() private _highlightIdx  = -1;
  @state() private _hasSearched   = false;   // true after first uwc-complete fire
  @state() private _inputFocused  = false;
  /** Slot presence — updated via MutationObserver for reliable detection. */
  @state() private _hasHeaderSlot = false;
  @state() private _hasFooterSlot = false;

  @query('#ac-input')       private _input!:       HTMLInputElement;
  @query('#input-root')     private _inputRoot!:   HTMLElement;
  @query('#panel')          private _panel!:       HTMLElement;
  @query('.options-scroll') private _optScroll?:   HTMLElement;

  private _keyboardNav       = false;
  /** Prevents updated() from overwriting _inputValue on internally-triggered value changes. */
  private _valueSyncDisabled = false;
  private _debounceTimer:    ReturnType<typeof setTimeout> | null = null;
  private _prevValue:        SelectOption | SelectOption[] | null = null;
  private _slotObserver?:    MutationObserver;

  private readonly _onPanelKeydown = this._handlePanelKeydown.bind(this);

  // ── Virtual scroll controller ──────────────────────────────────────────────

  private _vs = new VirtualScrollController<SelectOption>(this, {
    onRangeChange: () => { /* host.requestUpdate() called by controller */ },
  });

  // ── Placement controller ───────────────────────────────────────────────────

  private _pc = new PlacementController(this, {
    getPanelEl: () => this._panel,

    onTriggerFound: () => {
      // The input root is the trigger for positioning; we manage open/close
      // ourselves through typing events — no click binding needed here.
      return undefined;
    },

    dismissOnOutsideClick: true,
    dismissOnEscape:       true,

    afterShow: () => {
      this._highlightIdx = -1;
      document.addEventListener('keydown', this._onPanelKeydown);
      emit(this, 'uwc-show');

      requestAnimationFrame(() => {
        if (this.virtual && this._optScroll) {
          this._vs.attach(this._optScroll);
          this._vs.setItems(this.suggestions);
        }
      });
    },

    afterHide: () => {
      document.removeEventListener('keydown', this._onPanelKeydown);
      this._hasSearched  = false;
      emit(this, 'uwc-hide');

      // In force-selection mode, revert the input if no match was made.
      if (this.forceSelection) {
        const expected = this._labelForValue();
        if (this._inputValue !== expected) {
          this._inputValue = expected;
          this.value       = this._prevValue;
        }
      }
    },

    afterPosition: ({ triggerRect, panelEl }) => {
      panelEl.style.minWidth = `${triggerRect.width}px`;
    },
  });

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  override connectedCallback(): void {
    super.connectedCallback();
    const update = () => {
      this._hasHeaderSlot = !!this.querySelector('[slot="header"]');
      this._hasFooterSlot = !!this.querySelector('[slot="footer"]');
    };
    this._slotObserver = new MutationObserver(update);
    this._slotObserver.observe(this, { childList: true });
    update();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._slotObserver?.disconnect();
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    document.removeEventListener('keydown', this._onPanelKeydown);
  }

  override firstUpdated(): void {
    this._pc.setTriggerElement(this._inputRoot);
    if (!this.multiple && this.value) {
      this._inputValue = (this.value as SelectOption).label ?? '';
    }
    const hasVal = this._hasValue();
    this._validation.syncFormValue(hasVal ? JSON.stringify(this.value) : null);
    this._validation.revalidate(hasVal ? 'v' : '', { required: this.required });
    this.addEventListener('uwc-blur', () => this._validation.setTouched(true));
  }

  protected override updated(changed: PropertyValues): void {
    super.updated(changed);
    this._pc.updated(changed as Map<PropertyKey, unknown>, null, null);
    if (changed.has('required')) {
      this._validation.revalidate(this._hasValue() ? 'v' : '', { required: this.required });
    }

    // Sync input display value when value is set externally (parent → component).
    // Skipped when the value change originated from within the component itself.
    if (changed.has('value') && !this.multiple && !this._valueSyncDisabled) {
      const v = this.value as SelectOption | null;
      this._inputValue = v?.label ?? '';
    }
    this._valueSyncDisabled = false;

    // When suggestions arrive: update virtual scroll items.
    if (changed.has('suggestions')) {
      if (this.virtual && this._pc.open) {
        // Re-attach each time — _optScroll may be a new element if it was
        // previously absent (empty state renders no scroll container).
        if (this._optScroll) {
          this._vs.attach(this._optScroll);
          this._vs.setItems(this.suggestions);
        }
      }
      // If a search was pending and we now have results, make sure panel is open.
      if (this._hasSearched && this._inputFocused && !this._pc.open) {
        this._pc.show();
      }
    }

    // Keyboard-driven scroll-to-highlight.
    if (changed.has('_highlightIdx') && this._keyboardNav) {
      this._scrollToHighlighted();
      this._keyboardNav = false;
    }
  }

  // ── Value helpers ──────────────────────────────────────────────────────────

  private _hasValue(): boolean {
    if (this.multiple) return Array.isArray(this.value) && (this.value as SelectOption[]).length > 0;
    return this.value !== null && this.value !== undefined;
  }

  private _labelForValue(): string {
    if (!this._hasValue()) return '';
    if (this.multiple) return '';
    return (this.value as SelectOption)?.label ?? '';
  }

  private _isSelected(item: SelectOption): boolean {
    if (this.multiple) {
      return Array.isArray(this.value) &&
        (this.value as SelectOption[]).some(v => v.value === item.value);
    }
    return (this.value as SelectOption | null)?.value === item.value;
  }

  private _selectableItems(): SelectOption[] {
    return this.suggestions.filter(s => !s.group && !s.disabled);
  }

  // ── Selection ──────────────────────────────────────────────────────────────

  private _selectItem(item: SelectOption, e?: Event): void {
    if (item.disabled || item.group) return;

    this._prevValue        = this.value;
    this._valueSyncDisabled = true;   // we manage _inputValue ourselves

    if (this.multiple) {
      const current = Array.isArray(this.value) ? [...(this.value as SelectOption[])] : [];
      const idx     = current.findIndex(v => v.value === item.value);
      if (idx >= 0) {
        current.splice(idx, 1);
        emit<AutocompleteUnselectEventDetail>(this, 'uwc-unselect', { value: item, originalEvent: e });
      } else {
        current.push(item);
        emit<AutocompleteSelectEventDetail>(this, 'uwc-select', { value: item, originalEvent: e });
      }
      this.value       = current;
      this._inputValue = '';
      // Keep panel open for additional selections.
    } else {
      this.value       = item;
      this._inputValue = item.label;
      emit<AutocompleteSelectEventDetail>(this, 'uwc-select', { value: item, originalEvent: e });
      this._pc.hide();
    }

    emit(this, 'uwc-change', { value: this.value });
    const hasVal = this._hasValue();
    this._validation.handleChange(hasVal ? 'v' : '', { required: this.required });
    this._validation.setTouched(true);
    this._validation.syncFormValue(hasVal ? JSON.stringify(this.value) : null);
    this.requestUpdate();
  }

  private _removeChip(item: SelectOption, e: Event): void {
    e.stopPropagation();
    if (!this.multiple || !Array.isArray(this.value)) return;
    this.value = (this.value as SelectOption[]).filter(v => v.value !== item.value);
    emit<AutocompleteUnselectEventDetail>(this, 'uwc-unselect', { value: item, originalEvent: e });
    emit(this, 'uwc-change', { value: this.value });
    const hasVal = this._hasValue();
    this._validation.handleChange(hasVal ? 'v' : '', { required: this.required });
    this._validation.setTouched(true);
    this._validation.syncFormValue(hasVal ? JSON.stringify(this.value) : null);
    this.requestUpdate();
    this._input?.focus();
  }

  private _clear(e: Event): void {
    e.stopPropagation();
    const prev              = this.value;
    this._valueSyncDisabled = true;   // we manage _inputValue ourselves
    this.value              = this.multiple ? [] : null;
    this._inputValue        = '';
    emit<AutocompleteClearEventDetail>(this, 'uwc-clear', { previousValue: prev });
    emit(this, 'uwc-change', { value: this.value });
    this._validation.handleChange('', { required: this.required });
    this._validation.setTouched(true);
    this._validation.syncFormValue(null);
    this._pc.hide();
    this._input?.focus();
  }

  // ── Search / complete ──────────────────────────────────────────────────────

  private _scheduleComplete(query: string, e?: InputEvent): void {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }

    if (query.length < this.minLength) {
      this._hasSearched = false;
      if (this._pc.open) this._pc.hide();
      return;
    }

    this._debounceTimer = setTimeout(() => {
      this._hasSearched = true;
      emit<AutocompleteCompleteEventDetail>(this, 'uwc-complete', { query, originalEvent: e });
    }, this.delay);
  }

  private _handleInput(e: InputEvent): void {
    this._inputValue = (e.target as HTMLInputElement).value;
    if (!this.multiple) {
      this._valueSyncDisabled = true;   // clearing value; keep typed _inputValue
      this.value = null;
    }
    this._scheduleComplete(this._inputValue, e);
  }

  private _handleFocus(): void {
    this._inputFocused = true;
    emit(this, 'uwc-focus');
    // If dropdown button exists, show all on focus (completeOnFocus behaviour).
  }

  private _handleBlur(): void {
    this._inputFocused = false;
    emit(this, 'uwc-blur');
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
  }

  private _handleDropdownClick(): void {
    const query = this._inputValue;
    this._hasSearched = true;
    emit<AutocompleteCompleteEventDetail>(this, 'uwc-complete', { query });
    emit(this, 'uwc-dropdown-click', { query });
    if (!this._pc.open) this._pc.show();
    this._input?.focus();
  }

  // ── Keyboard navigation ────────────────────────────────────────────────────

  private _handleInputKeydown(e: KeyboardEvent): void {
    if (!this._pc.open) {
      if ((e.key === 'ArrowDown' || e.key === 'Enter') && this._hasSearched) {
        e.preventDefault();
        this._pc.show();
      }
      return;
    }

    const items = this._selectableItems();
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this._keyboardNav  = true;
        this._highlightIdx = items.length ? (this._highlightIdx + 1) % items.length : -1;
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._keyboardNav  = true;
        this._highlightIdx = items.length
          ? (this._highlightIdx - 1 + items.length) % items.length
          : -1;
        break;
      case 'Home':
        e.preventDefault();
        this._keyboardNav  = true;
        this._highlightIdx = 0;
        break;
      case 'End':
        e.preventDefault();
        this._keyboardNav  = true;
        this._highlightIdx = items.length - 1;
        break;
      case 'Enter':
        e.preventDefault();
        if (this._highlightIdx >= 0 && items[this._highlightIdx]) {
          this._selectItem(items[this._highlightIdx], e);
        }
        break;
      case 'Escape':
        // PlacementController handles Escape; just reset input if needed.
        break;
      case 'Tab':
        if (this._highlightIdx >= 0 && items[this._highlightIdx]) {
          this._selectItem(items[this._highlightIdx], e);
        } else {
          this._pc.hide();
        }
        break;
      case 'Backspace':
        if (this.multiple && this._inputValue === '' && Array.isArray(this.value) &&
            (this.value as SelectOption[]).length > 0) {
          const arr = this.value as SelectOption[];
          this._removeChip(arr[arr.length - 1], e);
        }
        break;
    }
  }

  // In case panel has focus for some reason.
  private _handlePanelKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this._pc.hide();
      this._input?.focus();
    }
  }

  // ── Scroll to highlighted ──────────────────────────────────────────────────

  private _scrollToHighlighted(): void {
    requestAnimationFrame(() => {
      if (this.virtual) {
        if (this._highlightIdx < 0) return;
        const target  = this._selectableItems()[this._highlightIdx];
        const filtIdx = target ? this.suggestions.indexOf(target) : -1;
        if (filtIdx >= 0) this._vs.scrollToIndex(filtIdx, 'auto');
        return;
      }
      const el = this.shadowRoot?.querySelector<HTMLElement>('[data-highlighted="true"]');
      el?.scrollIntoView({ block: 'nearest' });
    });
  }

  // ── Render helpers ─────────────────────────────────────────────────────────

  private _renderChips(): TemplateResult {
    if (!this.multiple || !Array.isArray(this.value)) return html``;
    const items    = this.value as SelectOption[];
    const visible  = items.slice(0, this.maxSelectedLabels);
    const overflow = items.length - visible.length;
    return html`
      <div class="chips-wrapper">
        ${visible.map(item => html`
          <span part="chip" class="chip">
            <span class="chip-label">${item.label}</span>
            <button
              type="button"
              class="chip-remove"
              aria-label="Remove ${item.label}"
              tabindex="-1"
              @click=${(e: Event) => this._removeChip(item, e)}
            >
              <uwc-icon name="close" size="1em"></uwc-icon>
            </button>
          </span>`)}
        ${overflow > 0 ? html`<span class="chip chip--overflow">+${overflow}</span>` : nothing}
      </div>`;
  }

  private _renderItemContent(item: SelectOption, isSelected: boolean): TemplateResult {
    return html`
      ${item.icon ? html`<span class="opt-icon">${item.icon}</span>` : nothing}
      <span class="opt-label">${item.label}</span>
      ${item.badge ? html`<span class="opt-badge">${item.badge}</span>` : nothing}
      ${isSelected
        ? html`<span class="opt-check" aria-hidden="true">
            <uwc-icon name="check" size="1em"></uwc-icon>
          </span>`
        : nothing}
    `;
  }

  private _renderSkeletonRows(): TemplateResult {
    return html`
      <ul class="options-list" role="presentation">
        ${[1, 2, 3].map(() => html`
          <li class="skeleton-item" aria-hidden="true">
            <span class="skeleton-bar" style="width: 60%"></span>
          </li>`)}
      </ul>`;
  }

  /** Standard (non-virtual) suggestions list. */
  private _renderSuggestionsStandard(): TemplateResult {
    if (this.loading) return this._renderSkeletonRows();

    if (!this.suggestions.length) {
      return html`<div part="empty" class="empty">${this.emptyMessage}</div>`;
    }

    const selectable = this._selectableItems();

    return html`
      <ul
        id="ac-listbox"
        role="listbox"
        aria-label="Suggestions"
        aria-multiselectable=${this.multiple}
        class="options-list"
      >
        ${repeat(
          this.suggestions,
          item => String(item.value ?? item.group ?? item.label),
          (item, absIdx) => {
            if (item.group !== undefined) {
              return html`
                <li part="group-header" class="group-header" role="presentation">
                  ${item.icon ? html`<span class="opt-icon">${item.icon}</span>` : nothing}
                  <span>${item.group || item.label}</span>
                </li>`;
            }

            const selIdx    = selectable.indexOf(item);
            const isHighlit = selIdx === this._highlightIdx;
            const isSel     = this._isSelected(item);

            return html`
              <li
                part="option"
                role="option"
                id="ac-opt-${String(item.value)}"
                aria-selected=${isSel}
                aria-disabled=${item.disabled ?? false}
                data-highlighted=${isHighlit}
                class=${classMap({
                  'option':              true,
                  'option--selected':    isSel,
                  'option--highlighted': isHighlit,
                  'option--disabled':    !!item.disabled,
                })}
                title=${item.title ?? ''}
                @click=${(e: Event) => { if (!item.disabled) this._selectItem(item, e); }}
                @mouseenter=${() => { if (!item.disabled) this._highlightIdx = selIdx; }}
              >
                ${this.renderItem
                  ? this.renderItem(item, absIdx)
                  : this._renderItemContent(item, isSel)}
              </li>`;
          },
        )}
      </ul>`;
  }

  /** Virtually-scrolled suggestions list. */
  private _renderSuggestionsVirtual(): TemplateResult {
    if (this.loading) {
      return html`
        <div class="options-scroll"
             style=${styleMap({ height: `${this.scrollHeight}px`, overflowY: 'auto' })}>
          ${this._renderSkeletonRows()}
        </div>`;
    }

    if (!this.suggestions.length) {
      return html`<div part="empty" class="empty">${this.emptyMessage}</div>`;
    }

    const selectable = this._selectableItems();
    const h          = `${this.scrollHeight}px`;

    return html`
      <div
        class="options-scroll"
        style=${styleMap({ height: h, overflowY: 'auto', overflowAnchor: 'none', position: 'relative' })}
        role="listbox"
        id="ac-listbox"
        aria-label="Suggestions"
        aria-multiselectable=${this.multiple}
      >
        <div style=${styleMap({ height: `${this._vs.paddingBefore}px` })} aria-hidden="true"></div>

        ${this._vs.visibleItems.map(({ item, index: absIdx }) => {
          if (item.group !== undefined) {
            return html`
              <div part="group-header" class="group-header" role="presentation"
                   style=${styleMap({ height: `${this.itemSize}px`, boxSizing: 'border-box' })}>
                ${item.icon ? html`<span class="opt-icon">${item.icon}</span>` : nothing}
                <span>${item.group || item.label}</span>
              </div>`;
          }

          const selIdx    = selectable.indexOf(item);
          const isHighlit = selIdx === this._highlightIdx;
          const isSel     = this._isSelected(item);

          return html`
            <div
              part="option"
              role="option"
              id="ac-opt-${String(item.value)}"
              aria-selected=${isSel}
              aria-disabled=${item.disabled ?? false}
              data-highlighted=${isHighlit}
              class=${classMap({
                'option':              true,
                'option--selected':    isSel,
                'option--highlighted': isHighlit,
                'option--disabled':    !!item.disabled,
              })}
              style=${styleMap({ height: `${this.itemSize}px`, boxSizing: 'border-box' })}
              title=${item.title ?? ''}
              @click=${(e: Event) => { if (!item.disabled) this._selectItem(item, e); }}
              @mouseenter=${() => { if (!item.disabled) this._highlightIdx = selIdx; }}
            >
              ${this.renderItem
                ? this.renderItem(item, absIdx)
                : this._renderItemContent(item, isSel)}
            </div>`;
        })}

        <div style=${styleMap({ height: `${this._vs.paddingAfter}px` })} aria-hidden="true"></div>
      </div>`;
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  protected override render() {
    const open       = this._pc.open;
    const hasValue   = this._hasValue();
    const floatActive = hasValue || this._inputFocused;

    const showClearBtn = this.showClear && hasValue && !this.disabled;
    const highlightedId = (() => {
      const items = this._selectableItems();
      return this._highlightIdx >= 0 && items[this._highlightIdx]
        ? `ac-opt-${String(items[this._highlightIdx].value)}`
        : undefined;
    })();

    const hasSlotHeader = this._hasHeaderSlot;
    const hasSlotFooter = this._hasFooterSlot;
    const hasPrefix     = !!this.querySelector('[slot="prefix"]');
    const hasSuffix     = !!this.querySelector('[slot="suffix"]');

    return html`
      <div
        id="input-root"
        part="input-root"
        class=${classMap({
          'ac-root':             true,
          'ac-root--open':       open,
          'ac-root--focused':    this._inputFocused,
          'ac-root--disabled':   this.disabled,
          'ac-root--invalid':    this.invalid,
          'ac-root--filled':     this.variant === 'filled',
          'ac-root--multiple':   this.multiple,
          'ac-root--has-prefix': hasPrefix,
          'ac-root--has-suffix': hasSuffix,
        })}
      >
        ${this.floatLabel && this.label
          ? html`<label
                   class=${classMap({
                     'float-label':        true,
                     'float-label--active': floatActive,
                   })}
                   aria-hidden="true"
                 >${this.label}</label>`
          : nothing}

        ${hasPrefix
          ? html`<span class="ac-prefix"><slot name="prefix"></slot></span>`
          : nothing}

        ${this.multiple ? this._renderChips() : nothing}

        <input
          id="ac-input"
          part="input"
          type="text"
          class="ac-input"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded=${open}
          aria-controls="ac-listbox"
          aria-activedescendant=${highlightedId ?? nothing}
          aria-label=${this.label || this.placeholder}
          aria-invalid=${this.invalid}
          .value=${live(this._inputValue)}
          placeholder=${this.placeholder}
          ?disabled=${this.disabled}
          @input=${this._handleInput}
          @focus=${this._handleFocus}
          @blur=${this._handleBlur}
          @keydown=${this._handleInputKeydown}
        />

        <span class="ac-actions">
          ${showClearBtn ? html`
            <span
              part="clear-btn"
              class="clear-btn"
              role="button"
              tabindex="-1"
              aria-label="Clear"
              @click=${this._clear}
            >
              <uwc-icon name="close" size="1em"></uwc-icon>
            </span>` : nothing}

          ${this.loading
            ? html`<span class="loading-icon" aria-hidden="true"></span>`
            : nothing}

          ${this.dropdown ? html`
            <button
              type="button"
              part="dropdown-btn"
              class="dropdown-btn"
              tabindex="-1"
              aria-label="Show suggestions"
              ?disabled=${this.disabled}
              @click=${this._handleDropdownClick}
            >
              <uwc-icon name="chevron-down" size="1em"></uwc-icon>
            </button>` : nothing}
        </span>

        ${hasSuffix
          ? html`<span class="ac-suffix"><slot name="suffix"></slot></span>`
          : nothing}
      </div>

      <div
        id="panel"
        part="panel"
        popover="manual"
        class="panel ${open ? 'is-open' : ''}"
        data-placement=${this.placement}
        aria-label="Suggestions"
        @mouseleave=${() => { this._highlightIdx = -1; }}
      >
        ${hasSlotHeader
          ? html`<div class="panel-header"><slot name="header"></slot></div>`
          : nothing}

        <div
          class="options-wrapper"
          style=${!this.virtual
            ? styleMap({ maxHeight: `${this.scrollHeight}px`, overflowY: 'auto' })
            : ''}
        >
          ${this.virtual
            ? this._renderSuggestionsVirtual()
            : this._renderSuggestionsStandard()}
        </div>

        ${hasSlotFooter
          ? html`<div class="panel-footer"><slot name="footer"></slot></div>`
          : nothing}
      </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-autocomplete': UwcAutocomplete; }
}
